/**
 * 公司列表页面 — CompanyList。
 *
 * GET /api/companies 获取列表；GET /api/brands 按 company_id 聚合品牌数。
 * DataTable 展示：公司名 / 行业 / 品牌数 / 网站 / 电话。
 * 点击行跳转 /companies/{id}。
 * 只读呈现（PRD 1.2 工作流：前端只读，skill 主录入）。
 *
 * TODO(概念稿待后端补字段): 角色徽章（运营方/客户）、产品数、
 * 本月诊断次数、最近活跃时间 —— 现有 GET /api/companies 未返回，
 * 待接口补充后按概念稿 companies.html 信息架构补齐列。
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  TextField,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import { SearchOutlined } from "@mui/icons-material";
import PageHeader from "../components/PageHeader";
import DataTable, { type DataTableColumn } from "../components/DataTable";
import Loading from "../components/Loading";
import { getCompanies } from "../api/companies";
import { getBrands } from "../api/brands";
import type { Company } from "../types";

export default function CompanyList() {
  const navigate = useNavigate();
  const [data, setData] = useState<Company[]>([]);
  const [brandCounts, setBrandCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  useEffect(() => {
    setLoading(true);
    setError("");
    getCompanies()
      .then(setData)
      .catch((err) =>
        setError(err instanceof Error ? err.message : "获取公司列表失败"),
      )
      .finally(() => setLoading(false));
    // 品牌数按 company_id 聚合（复用现有 GET /api/brands）
    getBrands()
      .then((brands) => {
        const counts: Record<string, number> = {};
        for (const b of brands) {
          counts[b.company_id] = (counts[b.company_id] || 0) + 1;
        }
        setBrandCounts(counts);
      })
      .catch(() => setBrandCounts({}));
  }, []);

  const columns: DataTableColumn<Company>[] = [
    {
      key: "name",
      label: "公司名称",
      render: (row) => (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: "primary.main" }}>
            {row.name}
          </Typography>
          {row.legal_name && row.legal_name !== row.name && (
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
              {row.legal_name}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: "industry",
      label: "行业",
      render: (row) => (
        <Typography sx={{ fontSize: 13 }}>{row.industry || "—"}</Typography>
      ),
    },
    {
      key: "brand_count",
      label: "品牌数",
      align: "center",
      render: (row) => (
        <Typography sx={{ fontSize: 13 }}>
          {brandCounts[row.id] ?? 0}
        </Typography>
      ),
    },
    {
      key: "website",
      label: "网站",
      render: (row) => (
        <Typography
          sx={{ fontSize: 12, color: "info.main", textDecoration: "underline" }}
        >
          {row.website || "—"}
        </Typography>
      ),
    },
    {
      key: "phone",
      label: "电话",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {row.phone || "—"}
        </Typography>
      ),
    },
  ];

  const filteredItems = data.filter((item) => {
    if (!searchText) return true;
    const text = searchText.toLowerCase();
    return (
      item.name.toLowerCase().includes(text) ||
      (item.legal_name || "").toLowerCase().includes(text) ||
      (item.industry || "").toLowerCase().includes(text)
    );
  });

  return (
    <Box>
      <PageHeader
        title="公司"
        breadcrumbs={[{ label: "资产管理" }, { label: "公司" }]}
      />

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="搜索公司名称、行业..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchOutlined
                      sx={{ color: "text.secondary", fontSize: 18, mr: 1 }}
                    />
                  ),
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Loading fullPage text="正在加载公司列表..." />
      ) : error ? (
        <Card>
          <CardContent>
            <Typography sx={{ fontSize: 13, color: "error.main" }}>
              {error}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <DataTable
          columns={columns}
          data={filteredItems}
          total={filteredItems.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(size) => {
            setRowsPerPage(size);
            setPage(0);
          }}
          onRowClick={(row) => navigate(`/companies/${row.id}`)}
          getRowId={(row) => row.id}
          emptyText="暂无公司数据"
        />
      )}
    </Box>
  );
}
